/**
 * Typed accessor for Vite environment variables used by the application.
 *
 * Wrapping `import.meta.env` in a dedicated module makes these values easy to
 * mock in Jest tests (which run under CommonJS and cannot access `import.meta`
 * directly).
 */

export const IS_MOCK: boolean = import.meta.env['VITE_USE_MOCK'] === 'true'

/** Known Gallery deployment environments mapped to their BFF base URL. */
type GalleryEnv = 'ed' | 'staging' | 'nightly' | 'prod'

export interface GalleryMixpanelConfig {
  token: string
  apiHost: string
  env: GalleryEnv
}

const SHARED_FILES_PATH = '/shared-files'
const ANALYTICS_PATH = '/analytics/v1'

/**
 * Gallery BFF base URLs per environment (without trailing slash).
 *
 * Mirrors the shell-core hostname → `jiveApiBaseUrl/shell-bff/v1` pattern for
 * each known environment.
 */
const SHELL_BFF_BASE_URLS: Record<GalleryEnv, string> = {
  ed: 'https://api.dev.jive.com/shell-bff/v1',
  staging: 'https://api.staging.jive.com/shell-bff/v1',
  nightly: 'https://api.jive.com/shell-bff/v1',
  prod: 'https://api.jive.com/shell-bff/v1',
}

const JIVE_API_BASE_URLS: Record<GalleryEnv, string> = {
  ed: 'https://api.dev.jive.com',
  staging: 'https://api.staging.jive.com',
  nightly: 'https://api.jive.com',
  prod: 'https://api.jive.com',
}

const MIXPANEL_TOKENS: Record<GalleryEnv, string> = {
  ed: '9ecffe57d3b75b7081a3f5d970a93259',
  staging: '9ecffe57d3b75b7081a3f5d970a93259',
  nightly: '9ecffe57d3b75b7081a3f5d970a93259',
  prod: 'c5934d98a88971c01e14163bfa6b1ee2',
}

/**
 * Detects the current Gallery environment from the page hostname.
 *
 * Detection order:
 * 1. Hostname contains `.ed.` → `'ed'`
 * 2. Hostname contains `.stage.` → `'staging'`
 * 3. Hostname contains `.nightly.` → `'nightly'`
 * 4. Hostname ends with `goto.com` → `'prod'`
 * 5. Anything else (e.g. `localhost`) → `'ed'` (dev fallback)
 */
function detectGalleryEnv(hostname: string): GalleryEnv {
  if (hostname.includes('.ed.')) {
    return 'ed'
  }
  if (hostname.includes('.stage.')) {
    return 'staging'
  }
  if (hostname.includes('.nightly.')) {
    return 'nightly'
  }
  if (hostname.endsWith('goto.com')) {
    return 'prod'
  }
  return 'ed'
}

/**
 * Returns the Gallery BFF base URL for the current runtime environment.
 *
 * In dev builds (`import.meta.env.DEV`), a non-empty `VITE_API_BASE_URL`
 * environment variable overrides hostname detection for local development.
 * Production builds always use hostname-based resolution.
 *
 * Unrecognised hostnames (including `localhost`) fall back to the ED dev
 * environment BFF URL.
 *
 * @returns The BFF base URL without a trailing slash.
 */
function getApiBaseUrl(): string {
  if (import.meta.env.DEV) {
    const override = (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? ''
    if (override) {
      return override
    }
  }
  return SHELL_BFF_BASE_URLS[detectGalleryEnv(window.location.hostname)]
}

/**
 * @returns The shared-files API base URL.
 */
export function getGalleryApiUrl(): string {
  return `${getApiBaseUrl()}${SHARED_FILES_PATH}`
}

export function getMixpanelConfig(): GalleryMixpanelConfig {
  const env = detectGalleryEnv(window.location.hostname)

  return {
    token: MIXPANEL_TOKENS[env],
    apiHost: `${JIVE_API_BASE_URLS[env]}${ANALYTICS_PATH}`,
    env,
  }
}
